import { Cookies } from 'quasar';
import {
  AppAuthTokenKey,
  AppAuthTokenCreatedKey,
  AppAuthRefeshTokenKey,
  AppAuthTokenExpireKey,
  ExpireCookieDays,
  SelectedStoreIdKey,
  StoreTimezoneKey,
} from '@/libs/constant';
import type { Token, IUser } from '@/types/models';
import { useAuthenStore } from '@/stores/authenStore';
import { useSSRContext } from 'vue';
import { useBase } from './useBase';
import { useLang } from './useLang';
import { useCache } from './useCache';
import { addDateByDays } from '@/utils/dateUtil';
import AuthenService from '@/api/AuthenService';
import { googleAuthCodeLogin } from 'vue3-google-login';
import { canRefreshToken, getTokenStatus } from '@/utils/jwtUtil';
import { AUTH_ROUTE_NAME, EMPLOYEE_ROUTE_NAME } from '@/libs/route-name';
export const useAuth = () => {
  const ssrContext = process.env.SERVER ? useSSRContext() : null;
  const cookies = process.env.SERVER ? Cookies.parseSSR(ssrContext) : Cookies; // otherwise we're on client
  const authenStore = useAuthenStore();
  const { appConfirm, appLoading, isDevMode, appNavigateTo, router } = useBase();
  const { t } = useLang();
  const { logoutClear, saveUserToCache, getUserFromCache, clearUserCache } = useCache();
  const { signinSocial } = AuthenService();
  const initAppAuthen = (): void => {
    // Load user from cache if available
    const cachedUser = getUserFromCache();
    if (cachedUser) {
      authenStore.setAuthen(cachedUser as IUser);
    }
  };
  const getAuthCookieByKey = (key: string): string | null => {
    if (!cookies) {
      return null;
    }
    return cookies.get(key) || null;
  };

  const setAuthen = (auth: IUser | null) => {
    if (auth != null) {
      authenStore.setAuthen(auth);
      saveUserToCache(auth);
    }
  };

  const signOut = async () => {
    if (!cookies) {
      return;
    }
    const conf = await appConfirm(t('app.monogram'), t('helper.logoutConfirm'));
    if (conf) {
      appLoading();
      clearUserCache();
      appLoading(false);
      destroyAuthDataAndRedirect();
    }
  };
  const setAuthenticationCookies = (authResponse: Token) => {
    setAuthCookies(cookies, authResponse, !isDevMode());
  };
  const setAuthCookies = (
    cookies: any,
    authResponse: Token,
    secure: boolean = false,
  ) => {
    if (cookies) {
      cookies.set(AppAuthTokenKey, authResponse.accessToken, {
        expires: addDateByDays(ExpireCookieDays),
        path: '/',
        // domain: secure ? AppDomain : null,
        // secure,
        sameSite: 'Lax',
      });

      cookies.set(AppAuthRefeshTokenKey, authResponse.refreshToken, {
        expires: addDateByDays(ExpireCookieDays),
        path: '/',
        // domain: secure ? AppDomain : null,
        // secure,
        sameSite: 'Strict',
      });

      // cookies.set(AppAuthTokenExpireKey, authResponse.expiresAt, {
      //   expires: addDateByDays(ExpireCookieDays),
      //   path: '/',
      //   // domain: secure ? AppDomain : null,
      //   secure,
      //   sameSite: 'Strict'
      // });

      cookies.set(AppAuthTokenCreatedKey, Date.now().toString(), {
        expires: addDateByDays(ExpireCookieDays),
        path: '/',
        // domain: secure ? AppDomain : null,
        // secure,
        sameSite: 'Strict',
      });
    }
  };

  const destroyAuthData = () => {
    return new Promise((resolve) => {
      if (cookies) {
        cookies.remove(AppAuthTokenKey, { path: '/' });
        cookies.remove(AppAuthRefeshTokenKey, { path: '/' });
        cookies.remove(AppAuthTokenExpireKey, { path: '/' });
        cookies.remove(AppAuthTokenCreatedKey, { path: '/' });
      }
      logoutClear();
      resolve(true);
    });
  };
  const destroyAuthDataAndRedirect = async (
    forceRedirectToLoginPage = true,
  ) => {
    await destroyAuthData();
    clearUserCache();
    localStorage.removeItem(SelectedStoreIdKey);
    localStorage.removeItem(StoreTimezoneKey);
    authenStore.logout();
    if (forceRedirectToLoginPage) {
      router.push({ name: AUTH_ROUTE_NAME.SIGNIN });
    }
  };

  const signInFacebook = async () => {
    const res: any = await new Promise((resolve, reject) => {
      window.FB.login(
        (res: any) => {
          res.authResponse
            ? resolve(res)
            : reject('User cancelled login or did not authorize');
        },
        { scope: 'public_profile,email' },
      );
    });
    const accessToken = res.authResponse.accessToken;
    const response = await signinSocial('/api/v1/auth/login-facebook', {
      accessToken,
    });
    authSuccess(response);
  };

  const signInGoogle = async () => {
    const { code } = await googleAuthCodeLogin();
    if (!code)
      return 0;
    const response = await signinSocial('/api/v1/auth/login-google', {
      authCode: code,
    });
    authSuccess(response);
  };

  const authSuccess = async (response: any) => {
    if (response && response.data.token) {
      setAuthenticationCookies(response.data.token);
      setAuthen(response.data.user);
      // appNavigateTo(EMPLOYEE_ROUTE_NAME.LIST_EMPLOYEE, true);
      // window.location.replace(EMPLOYEE_ROUTE_NAME.LIST_EMPLOYEE || '/');
      router.push({ name: EMPLOYEE_ROUTE_NAME.PICK_EMPLOYEES })
    }
  };

  const isUserLoggedIn = () => {
    const cachedUser = getUserFromCache();
    const token = getAuthCookieByKey(AppAuthTokenKey);
    return !!(cachedUser && token);
  };

  const checkLoginStatus = async () => {
    const cachedUser = getUserFromCache();
    const token = getAuthCookieByKey(AppAuthTokenKey);
    if (!cachedUser || !token) {
      return false;
    }
    const tokenStatus = await getTokenStatus(token);
    if (tokenStatus === 'VALID') {
      const needsRefresh = await canRefreshToken(token);
      if (needsRefresh) {
        const refreshResult = await authenStore.refreshToken();
        if (!refreshResult.status) {
          return false;
        }
      }
      return true;
    }
    destroyAuthDataAndRedirect();
    return false;
  };

  const getCurrentUser = (): IUser | null => {
    return getUserFromCache() as IUser;
  };


  return {
    initAppAuthen,
    signOut,
    setAuthen,
    signInGoogle,
    signInFacebook,
    destroyAuthDataAndRedirect,
    setAuthenticationCookies,
    authSuccess,
    getAuthCookieByKey,
    getUserFromCache,
    clearUserCache,
    isUserLoggedIn,
    getCurrentUser,
    checkLoginStatus,
  };
};
